/**
 * Where a client's published advisor summary stands, kept apart on purpose:
 * "still loading", "could not load" and "nothing published" all used to be one
 * `null`, so the report could be printed — silently without its summary —
 * before the summary had even arrived.
 */
import type { PublishedAdvisorSummary } from '@agio/core/domain';

export type AdvisorSummaryState =
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'none' }
  | { status: 'ready'; summary: PublishedAdvisorSummary };

/** The last answer the server gave, and which request it answered. */
export interface AdvisorFetch {
  path: string;
  outcome: { ok: true; data: PublishedAdvisorSummary | null } | { ok: false; error: string };
}

export function advisorSummaryPath(clientId: string | null): string | null {
  return clientId ? `/clients/${encodeURIComponent(clientId)}/advisor-summary` : null;
}

/**
 * The state for `path` given the last answer. An answer to a DIFFERENT request
 * (the previous client's, say) says nothing about this one: still loading.
 */
export function advisorSummaryState(path: string | null, last: AdvisorFetch | null): AdvisorSummaryState {
  if (path === null) return { status: 'none' };
  if (last === null || last.path !== path) return { status: 'loading' };
  if (!last.outcome.ok) return { status: 'error', error: last.outcome.error };
  return last.outcome.data ? { status: 'ready', summary: last.outcome.data } : { status: 'none' };
}

/** The printed report may only be printed once the summary question is settled — either way. */
export function canPrintWith(state: AdvisorSummaryState): boolean {
  return state.status === 'ready' || state.status === 'none';
}
